/**
 * ReactivateInstrumentConfirmationModal.tsx
 * Lightweight confirmation modal for reactivating inactive instruments
 *
 * Used by InstrumentDropdown when a user selects an inactive instrument.
 * Prompts user to confirm reactivation before completing the selection.
 */

// 1. External dependencies
import React from 'react';
import { RefreshCw } from 'lucide-react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from '@enfomentis/ui';

/**
 * Props for ReactivateInstrumentConfirmationModal
 */
export interface ReactivateInstrumentConfirmationModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Handler for closing the modal (cancel action) */
  onClose: () => void;
  /** Handler for confirming reactivation */
  onConfirm: () => void;
  /** Instrument symbol to display */
  instrumentSymbol: string;
  /** Optional display name (shown if different from symbol) */
  instrumentDisplayName?: string | undefined;
}

/**
 * Confirmation modal for reactivating an inactive instrument
 */
export const ReactivateInstrumentConfirmationModal: React.FC<
  ReactivateInstrumentConfirmationModalProps
> = ({ isOpen, onClose, onConfirm, instrumentSymbol, instrumentDisplayName }) => {
  // Build display text
  const displayText =
    instrumentDisplayName && instrumentDisplayName !== instrumentSymbol
      ? `${instrumentSymbol} (${instrumentDisplayName})`
      : instrumentSymbol;

  return (
    <Modal isOpen={isOpen} onOpenChange={(open) => !open && onClose()} size="sm">
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex gap-2 items-center">
              <RefreshCw className="w-5 h-5 text-primary" />
              <span>Reactivate Instrument?</span>
            </ModalHeader>

            <ModalBody>
              <p className="text-sm text-text-secondary">
                <strong>"{displayText}"</strong> is currently inactive.
              </p>
              <p className="text-sm text-text-secondary mt-2">
                Would you like to reactivate it so it can be used in your trading workflow?
              </p>
            </ModalBody>

            <ModalFooter>
              <Button variant="light" onPress={onClose}>
                Cancel
              </Button>
              <Button
                color="primary"
                onPress={onConfirm}
              >
                Reactivate
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ReactivateInstrumentConfirmationModal;
